// Hazard icons v2: every hazard icon — the map marker and the badges in the
// panels — is a warning triangle from the HiiKER map style's sprite, named
// `<slug>_<passability>` (e.g. `tree_blocked`). The colour says whether hikers
// can get past: red when blocked, orange when passable. Confirmed and
// unconfirmed look the same.
//
// The map reads the name straight off the tile (`icon`, built by
// db/functions/get_trail_hazards_v2.sql from the same slug table) and Mapbox
// resolves it from the style's sprite, like the POI layer's `puck_icon_*`.
// Outside the map the same names pick the bundled PNG crops of those icons
// (images/hazards/<name>.png, see hazardIconAssets.ts).

import { HAZARD_TYPES, HazardType, PASSABILITIES, Passability, hazardTypeFromWire, passabilityFromWire } from './hazardTypes';

/** hazard_type -> the sprite's slug (ICONS.md; Hazard::SPRITE_SLUGS on the server). */
export const HAZARD_SPRITE_SLUGS: Readonly<Record<HazardType, string>> = Object.freeze({
  flooded_path: 'flooded',
  tree_down: 'tree',
  path_closed: 'closed',
  livestock_wildlife: 'wildlife',
  path_hard_to_follow: 'hard',
  boggy_ground: 'boggy',
  heavy_vegetation: 'overgrown',
  rockfall_landslip: 'rockfall',
  ice_snow: 'ice',
  bridge_damage: 'bridge',
  fire: 'fire',
});

/**
 * Where the passability isn't known (a type on its own), the orange
 * `_passable` triangle is the neutral icon for the type.
 */
export const NEUTRAL_PASSABILITY: Passability = 'passable';

/**
 * The sprite's generic warning icon (a red disc with "!"), for a warning that
 * isn't a known hazard type — a newer server's type this bundle can't name.
 */
export const GENERIC_WARNING_ICON = 'puck_icon_warning';

/**
 * `tree_down` + `blocked` -> `tree_blocked`. Accepts the wire spellings
 * (`TREE_DOWN` / `BLOCKED`). An unknown or missing passability gives the
 * neutral `_passable` variant; an unknown type gives null (callers fall back
 * to GENERIC_WARNING_ICON) — never a name the sprite doesn't have.
 */
export function hazardSpriteName(type: unknown, passability?: unknown): string | null {
  const hazardType = hazardTypeFromWire(type);
  if (!hazardType) return null;
  const state = passabilityFromWire(passability) ?? NEUTRAL_PASSABILITY;
  return `${HAZARD_SPRITE_SLUGS[hazardType]}_${state}`;
}

/** Like hazardSpriteName, but always an icon: the generic warning for an unknown type. */
export function hazardIconName(type: unknown, passability?: unknown): string {
  return hazardSpriteName(type, passability) ?? GENERIC_WARNING_ICON;
}

/** Every hazard marker name a tile can carry: each type × each passability. */
export const HAZARD_SPRITE_NAMES: readonly string[] = Object.freeze(
  HAZARD_TYPES.flatMap((type) => PASSABILITIES.map((state) => `${HAZARD_SPRITE_SLUGS[type]}_${state}`)),
);

const NAME_SET = new Set(HAZARD_SPRITE_NAMES);

/** Whether a style image id is one of the hazard markers (and nothing else). */
export function isHazardSpriteName(id: unknown): id is string {
  return typeof id === 'string' && NAME_SET.has(id);
}
